'use client';

import { faCalendarAlt, faChair, faClock, faEuroSign } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useModal } from '@hooks/useModal';
import { Badge, Blockquote, Button } from '@ui';
import BookingAssistant from 'components/SpaceManager/BookingAssistant';
import { TFunction } from 'i18next';
import { motion } from 'framer-motion';
import moment from 'moment';
// German dates on a club website too, which no longer loads the app's shell (the
// global import of this locale lives there).
import 'moment/locale/de';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { ExtendedSlot } from '~/components/SpaceManager/Views/TableView';
import { ClubAdminViewRecord } from './types';
import { stringifyValue } from './utils';
import { bookingModalChrome } from 'components/SpaceManager/BookingAssistant/modalChrome';

const getViewColumnMap = (t: TFunction<'ai'>): Record<string, Array<{ key: string; label: string }>> => ({
    members: [
        { key: 'id', label: t('viewRenderers.columns.members.id') },
        { key: 'name', label: t('viewRenderers.columns.members.name') },
        { key: 'email', label: t('viewRenderers.columns.members.email') },
        { key: 'number', label: t('viewRenderers.columns.members.number') },
        { key: 'status', label: t('viewRenderers.columns.members.status') }
    ],
    bookings: [
        { key: 'id', label: t('viewRenderers.columns.bookings.id') },
        { key: 'event', label: t('viewRenderers.columns.bookings.event') },
        { key: 'datum', label: t('viewRenderers.columns.bookings.date') },
        { key: 'nutzer', label: t('viewRenderers.columns.bookings.user') },
        { key: 'status', label: t('viewRenderers.columns.bookings.status') }
    ],
    allocations: [
        { key: 'id', label: t('viewRenderers.columns.allocations.id') },
        { key: 'platz', label: t('viewRenderers.columns.allocations.space') },
        { key: 'datum', label: t('viewRenderers.columns.allocations.date') },
        { key: 'zeit', label: t('viewRenderers.columns.allocations.time') },
        { key: 'nutzer', label: t('viewRenderers.columns.allocations.user') }
    ],
    invoices: [
        { key: 'number', label: t('viewRenderers.columns.invoices.number') },
        { key: 'debtor', label: t('viewRenderers.columns.invoices.debtor') },
        { key: 'datum', label: t('viewRenderers.columns.invoices.date') },
        { key: 'due_date', label: t('viewRenderers.columns.invoices.dueDate') },
        { key: 'amount', label: t('viewRenderers.columns.invoices.amount') },
        { key: 'status', label: t('viewRenderers.columns.invoices.status') },
        { key: '__actions', label: t('viewRenderers.columns.invoices.actions') }
    ],
    space_groups: [
        { key: 'id', label: t('viewRenderers.columns.spaceGroups.id') },
        { key: 'name', label: t('viewRenderers.columns.spaceGroups.name') }
    ],
    inquiries: [
        { key: 'typ', label: t('viewRenderers.columns.inquiries.type') },
        { key: 'name', label: t('viewRenderers.columns.inquiries.name') },
        { key: 'status', label: t('viewRenderers.columns.inquiries.status') },
        { key: 'eingegangen', label: t('viewRenderers.columns.inquiries.receivedAt') }
    ],
    course_popularity: [
        { key: 'kurs', label: t('viewRenderers.columns.coursePopularity.course') },
        { key: 'start', label: t('viewRenderers.columns.coursePopularity.start') },
        { key: 'termine_im_zeitraum', label: t('viewRenderers.columns.coursePopularity.datesInPeriod') },
        { key: 'buchungen', label: t('viewRenderers.columns.coursePopularity.bookings') },
        { key: 'max_teilnehmer', label: t('viewRenderers.columns.coursePopularity.maxParticipants') },
        { key: 'auslastung', label: t('viewRenderers.columns.coursePopularity.utilization') }
    ]
});

const InvoiceActions = ({ invoiceId }: { invoiceId: number }) => {
    const { data } = useSWR(invoiceId ? `invoice/${invoiceId}` : null);
    const invoice = data?.data;

    const handleOpenPdf = () => {
        if (!invoice?.url) return;
        const popup = window.open(invoice.url, '_blank');
        if (popup) popup.focus();
    };

    if (!invoice?.url) return null;

    return (
        <Button size="xs" flat color="secondary" onPress={handleOpenPdf} className="!tw-rounded-lg">
            PDF
        </Button>
    );
};

type SpaceAllocation = { start: string; end: string };
type SpaceBookingState = { slot: ExtendedSlot | null; canBook: boolean; label: string; description: string };

const SPACE_DATE_FORMATS = [moment.ISO_8601, 'YYYY-MM-DD', 'DD.MM.YYYY', 'DD.MM.YYYY HH:mm'] as const;

const getSpaceBookingSlot = (item: ClubAdminViewRecord): ExtendedSlot | null => {
    const spaceId = item.space_id ?? item.id;
    const rawDate = item.date_iso ?? item.current_date ?? item.currentDate ?? item.date ?? item.datum;

    if (!spaceId || !rawDate) return null;

    const currentDate =
        rawDate instanceof Date ? moment(rawDate) : moment(String(rawDate), [...SPACE_DATE_FORMATS], true);

    if (!currentDate.isValid()) return null;

    const now = moment();
    const startMinutes = currentDate.isSame(now, 'day')
        ? Math.min(Math.ceil((now.hours() * 60 + now.minutes()) / 30) * 30, 23 * 60 + 30)
        : 9 * 60;

    return { start: startMinutes, width: 60, data: null, currentDate: currentDate.toDate(), spaceId: Number(spaceId) };
};

const getSpaceBookingState = (item: ClubAdminViewRecord, t: TFunction<'ai'>): SpaceBookingState => {
    const slot = getSpaceBookingSlot(item);

    if (!slot) {
        return {
            slot: null,
            canBook: false,
            label: t('viewRenderers.space.bookingUnavailable.label'),
            description: t('viewRenderers.space.bookingUnavailable.description')
        };
    }

    if (moment(slot.currentDate).endOf('day').isBefore(moment())) {
        return {
            slot: null,
            canBook: false,
            label: t('viewRenderers.space.bookingPast.label'),
            description: t('viewRenderers.space.bookingPast.description')
        };
    }

    return {
        slot,
        canBook: true,
        label: t('viewRenderers.space.bookingAvailable.label'),
        description: t('viewRenderers.space.bookingAvailable.description')
    };
};

const SpaceCard = ({ item, clubSlug }: { item: ClubAdminViewRecord; clubSlug: string }) => {
    const { t } = useTranslation('ai');
    const [setModal, resetModal] = useModal();
    const allocations = Array.isArray(item.belegungen) ? (item.belegungen as SpaceAllocation[]) : [];
    const bookingState = getSpaceBookingState(item, t);
    const statusText = String(item.status ?? '');
    const isFrei = statusText.toLowerCase() === 'frei';

    const handleBuchen = () => {
        if (!bookingState.slot) return;
        setModal({
            title: t('viewRenderers.space.bookModalTitle', { name: String(item.name) }),
            content: <BookingAssistant clubSlug={clubSlug} slot={bookingState.slot} callback={() => resetModal()} />,
            ...bookingModalChrome,
            show: true
        });
    };

    return (
        <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-4">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                <div>
                    <div className="tw-font-semibold tw-text-accents-9">{String(item.name)}</div>
                    <div className="tw-mt-0.5 tw-text-xs tw-text-accents-6">{String(item.datum)}</div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {/* Not `warning`: its amber is the app's own and no repaint rule
                        reaches it, so a taken slot announced itself in a colour the
                        club never picked. */}
                    <Badge flat color={isFrei ? 'success' : 'light'}>
                        {statusText || t('viewRenderers.space.statusUnknown')}
                    </Badge>
                    <Badge flat color={bookingState.canBook ? 'success' : 'secondary'}>
                        {bookingState.label}
                    </Badge>
                </div>
            </div>
            <div className="tw-mt-3 tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-accents-0 tw-p-3">
                <div className="tw-min-w-0">
                    <div className="tw-text-sm tw-font-medium tw-text-accents-8">
                        {bookingState.canBook
                            ? t('viewRenderers.space.directBookingAvailable')
                            : t('viewRenderers.space.directBookingUnavailable')}
                    </div>
                    <div className="tw-mt-1 tw-text-xs tw-leading-5 tw-text-accents-6">{bookingState.description}</div>
                </div>
                <Button
                    size="xs"
                    color={bookingState.canBook ? 'success' : 'light'}
                    block
                    disabled={!bookingState.canBook}
                    onPress={handleBuchen}
                    className="!tw-shrink-0 !tw-rounded-lg"
                >
                    {t('viewRenderers.space.bookButton')}
                </Button>
            </div>
            {allocations.length > 0 ? (
                <div className="tw-mt-3">
                    <div className="tw-mb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accents-6">
                        {t('viewRenderers.space.occupied')}
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                        {allocations.map((a, i) => (
                            <span
                                key={i}
                                className="tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-warning/30 tw-bg-warning/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-warning"
                            >
                                {a.start}–{a.end}
                            </span>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="tw-mt-3 tw-text-xs tw-text-accents-5">{t('viewRenderers.space.noOccupancy')}</div>
            )}
        </div>
    );
};

export const SpacesCardView = ({ items = [], clubSlug }: { items?: ClubAdminViewRecord[]; clubSlug: string }) => {
    const { t } = useTranslation('ai');
    if (items.length === 0) return <Blockquote color="light">{t('viewRenderers.space.empty')}</Blockquote>;

    return (
        <div className="tw-grid tw-gap-3">
            {items.map((item, index) => (
                <SpaceCard key={String(item.id ?? `${item.name}-${index}`)} item={item} clubSlug={clubSlug} />
            ))}
        </div>
    );
};

const EventCard = ({ item, clubSlug }: { item: ClubAdminViewRecord; clubSlug: string }) => {
    const { t } = useTranslation('ai');
    const isAvailable = item.status !== 'Ausgebucht';
    const href = item.slug ? `${process.env.NEXT_PUBLIC_HOST}club/${clubSlug}/bookings/${item.slug}` : null;
    return (
        <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-4">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                <div className="tw-min-w-0">
                    <div className="tw-font-semibold tw-leading-snug tw-text-accents-9">{String(item.title ?? '')}</div>
                    <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-accents-6">
                        {item.datum && (
                            <span className="tw-flex tw-items-center tw-gap-1">
                                <FontAwesomeIcon icon={faCalendarAlt} className="tw-text-accents-5" />
                                {String(item.datum)}
                            </span>
                        )}
                        {item.zeit && (
                            <span className="tw-flex tw-items-center tw-gap-1">
                                <FontAwesomeIcon icon={faClock} className="tw-text-accents-5" />
                                {String(item.zeit)}
                            </span>
                        )}
                        {item.preis && (
                            <span className="tw-flex tw-items-center tw-gap-1">
                                <FontAwesomeIcon icon={faEuroSign} className="tw-text-accents-5" />
                                {String(item.preis)}
                            </span>
                        )}
                        {item.plaetze && (
                            <span className="tw-flex tw-items-center tw-gap-1">
                                <FontAwesomeIcon icon={faChair} className="tw-text-accents-5" />
                                {t('viewRenderers.event.seatsFree', { count: Number(item.plaetze) || 0 })}
                            </span>
                        )}
                    </div>
                </div>
                <Badge flat color={isAvailable ? 'success' : 'error'} className="tw-shrink-0">
                    {String(item.status ?? '')}
                </Badge>
            </div>
            {item.beschreibung && (
                <p className="tw-mt-2 tw-line-clamp-2 tw-text-xs tw-leading-5 tw-text-accents-7">
                    {String(item.beschreibung)}
                </p>
            )}
            {href && isAvailable && (
                <div className="tw-mt-3">
                    <Link href={href}>
                        {/* Solid rather than flat: `flat` draws in `successLight/70`
                            over a `success/10` edge, which the widget stylesheet cannot
                            repaint, while the solid pair already maps to the club's
                            primary. */}
                        <Button size="xs" color="success" block className="!tw-rounded-lg">
                            {t('viewRenderers.event.registerButton')}
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
};

export const EventsCardView = ({ items = [], clubSlug }: { items?: ClubAdminViewRecord[]; clubSlug: string }) => {
    const { t } = useTranslation('ai');
    if (items.length === 0) return <Blockquote color="light">{t('viewRenderers.event.empty')}</Blockquote>;
    return (
        <div className="tw-grid tw-gap-3">
            {items.map((item, index) => (
                <EventCard key={String(item.id ?? index)} item={item} clubSlug={clubSlug} />
            ))}
        </div>
    );
};

export const ViewTable = ({ type, items = [] }: { type: string; items?: ClubAdminViewRecord[] }) => {
    const { t } = useTranslation('ai');
    const columns = getViewColumnMap(t)[type] || [];

    if (items.length === 0) return <Blockquote color="light">{t('viewRenderers.table.empty')}</Blockquote>;

    return (
        <div className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white">
            <div className="tw-overflow-x-auto">
                <table className="tw-min-w-full tw-border-collapse tw-text-left tw-text-sm">
                    <thead className="tw-bg-accents-0 tw-text-accents-8">
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide"
                                >
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr
                                key={`${type}-${index}`}
                                className="tw-border-0 tw-border-t tw-border-solid tw-border-accents-2"
                            >
                                {columns.map((column) => (
                                    <td key={column.key} className="tw-px-4 tw-py-3 tw-align-top tw-text-accents-8">
                                        <div className="tw-min-h-6 tw-py-1">
                                            {column.key === '__actions' &&
                                            type === 'invoices' &&
                                            typeof item.id === 'number' ? (
                                                <InvoiceActions invoiceId={item.id} />
                                            ) : (
                                                stringifyValue(item[column.key], t)
                                            )}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const StatisticsView = ({ items = [] }: { items?: ClubAdminViewRecord[] }) => {
    const { t } = useTranslation('ai');
    if (items.length === 0) return <Blockquote color="light">{t('viewRenderers.statistics.empty')}</Blockquote>;

    return (
        <div className="tw-grid tw-gap-3 md:tw-grid-cols-2">
            {items.map((item, index) => (
                <motion.div
                    key={`statistics-${index}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03, duration: 0.2 }}
                    className="tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-p-4"
                >
                    <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accents-6">
                        {stringifyValue(item.label, t)}
                    </div>
                    <div className="tw-mt-2 tw-text-2xl tw-font-semibold tw-leading-none tw-text-accents-9">
                        {stringifyValue(item.value, t)}
                    </div>
                </motion.div>
            ))}
        </div>
    );
};
